import type { EvidencePolicy } from './governed-capability-manifest';

// ---------------------------------------------------------------------------
// #5168 — retrieval capability contract (additive; carried per intent).
// ---------------------------------------------------------------------------
// A retrieval capability lets the manager fetch missing evidence itself
// instead of asking the executive for it. It is declared exactly like any
// other intent property — the manager never learns whether the fetch runs
// in-process, through an internal service, an approved connector, or an
// MCP adapter; all of that lives behind the same governed executor.
//
// #5841 size-law extraction (terra HOLD, PR #5860 comment 5474395860,
// finding "touched oversize file violated size law"): moved out of
// `governed-capability-manifest.ts` verbatim — same exports, same shapes,
// same behavior, re-exported there and from the package barrel so every
// existing import path (`@helix/lib` and the direct file path) is unaffected.
export const RETRIEVAL_FAILURE_MODES = [
  'timeout',
  'permission_denied',
  'source_unavailable',
  'limit_exceeded',
  'not_lawful',
  'untrusted_content_rejected',
  // #5168 r2 — a zero-evidence answer is a closed, truthful outcome, never
  // folded into an answered/admitted observation.
  'empty_result',
] as const;

export type RetrievalFailureMode = (typeof RETRIEVAL_FAILURE_MODES)[number];

/**
 * Whether a successful retrieval alone can make the fetched evidence
 * admissible, or whether it still needs a human review/promotion step, or
 * whether it never becomes evidence at all and only informs the manager's
 * next decision as a transient observation.
 */
export const RETRIEVAL_LIFECYCLE_ENTRIES = [
  'admissible',
  'review_required',
  'promotion_required',
  'transient_observation',
] as const;

export type RetrievalLifecycleEntry = (typeof RETRIEVAL_LIFECYCLE_ENTRIES)[number];

export type RetrievalCapabilityDeclaration = Readonly<{
  /** The evidence key(s) a successful call can satisfy. */
  evidenceKeys: readonly string[];
  /** Input scope/filter names the caller may narrow the read by. */
  scopeFilters: readonly string[];
  sourceClass: string;
  permissions: readonly string[];
  sideEffectClass: 'read_only';
  outputSchemaRef: string;
  freshness: EvidencePolicy;
  /** Bounded row/document ceiling a single call may return. */
  boundedResultLimit: number;
  failureModes: readonly RetrievalFailureMode[];
  lifecycleEntry: RetrievalLifecycleEntry;
}>;
